// Voice Mode — hands-free voice conversation with HBM.
//
// Flow: hold or click the big orb to speak → auto-transcribes on stop
// → sends the transcript to /api/chat → streams the reply → speaks it
// back through /api/tts (Sarvam Bulbul v3) → returns to idle.
//
// Session is auto-saved to /api/history on every turn (kind="voice")
// so admins can revisit spoken sessions from the sidebar. Same schema
// as the written chat — the sidebar just tags voice rows with a pill.

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Mic, Square, Loader2, Volume2, VolumeX, Download, FileDown } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  isVoiceEnabledFor,
  isVoiceSupported,
  startRecording,
  transcribeAudio,
  speakText,
} from "@/lib/voice";
import { streamChat, stripMarkdown } from "@/lib/api";
import { newConversationId, makeTitle } from "@/lib/history";
import { historyApi } from "@/lib/historyApi";
import { downloadAudio, downloadTranscript } from "@/lib/voice";
import { RealtimeSession, isDirectVoiceEnabledFor } from "@/lib/realtime";

// UI states drive the big orb and status caption.
const STATES = {
  IDLE: "idle",
  LISTENING: "listening",
  TRANSCRIBING: "transcribing",
  THINKING: "thinking",
  SPEAKING: "speaking",
};

export default function VoiceMode() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [state, setState] = useState(STATES.IDLE);
  const [transcript, setTranscript] = useState("");        // latest user utterance
  const [assistantText, setAssistantText] = useState("");  // latest reply (streamed)
  const [history, setHistory] = useState([]);              // [{role, content}]
  const [muted, setMuted] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  // Voice mode selector — "standard" (Whisper STT + Sarvam TTS) or
  // "direct" (OpenAI Realtime WebRTC, full duplex). Preference is
  // remembered per browser via localStorage. Direct is only reachable
  // when the backend flag `direct_voice_beta` is set on the user.
  const directAllowed = isDirectVoiceEnabledFor(user);
  const [voiceMode, setVoiceMode] = useState(() => {
    if (typeof window === "undefined") return "standard";
    const saved = window.localStorage.getItem("hbm.voiceMode");
    return saved === "direct" ? "direct" : "standard";
  });
  // Direct Voice runtime state — kept separate from the standard-voice
  // STATES so the two paths can coexist without their state machines
  // stepping on each other.
  const [directConnected, setDirectConnected] = useState(false);
  const [directConnecting, setDirectConnecting] = useState(false);
  // Holds the final mixed session recording (mic + HBM) after the
  // Direct Voice session ends, so the "Save audio" button remains
  // functional even after the user hangs up. Cleared when a new
  // session starts.
  const [directSessionBlob, setDirectSessionBlob] = useState(null);
  // Live capture size — visible in the button label so the user can
  // SEE that recording is actually happening. Polls once per second
  // from the active RealtimeSession's session-mix recorder buffer.
  const [captureBytes, setCaptureBytes] = useState(0);
  const [captureStatus, setCaptureStatus] = useState("idle"); // "idle" | "recording" | "failed"
  const directRef = useRef(null);

  // Poll the active RealtimeSession for buffer size once per second
  // while Direct Voice is connected. Purely for the diagnostic label —
  // gives the user immediate visual proof that audio is (or isn't)
  // being captured.
  useEffect(() => {
    if (!directConnected) {
      setCaptureBytes(0);
      return;
    }
    const tick = () => {
      const rec = directRef.current;
      if (!rec) return;
      const blob = rec._sessionChunks
        ? new Blob(rec._sessionChunks)
        : null;
      setCaptureBytes(blob ? blob.size : 0);
      setCaptureStatus(
        rec._sessionRecorder && rec._sessionRecorder.state === "recording"
          ? "recording"
          : "failed",
      );
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [directConnected]);

  const recRef = useRef(null);
  const speakRef = useRef(null);
  const abortRef = useRef(null);
  const sessionIdRef = useRef(newConversationId());
  const timerRef = useRef(null);
  const stateRef = useRef(state);
  stateRef.current = state;
  const createdAtRef = useRef(new Date().toISOString());
  const titleRef = useRef("");

  // Persist the current voice conversation to /api/history so it shows
  // up in the sidebar alongside written chats. Best-effort — failures
  // are logged but never surface to the user (voice keeps working).
  const persistTurn = async (fullHistory) => {
    if (!fullHistory.length) return;
    if (!titleRef.current) {
      const firstUser = fullHistory.find((m) => m.role === "user");
      titleRef.current = firstUser ? makeTitle(firstUser.content) : "Voice session";
    }
    try {
      await historyApi.upsert({
        id: sessionIdRef.current,
        title: titleRef.current,
        messages: fullHistory,
        createdAt: createdAtRef.current,
        updatedAt: new Date().toISOString(),
        projectId: null,
        kind: "voice",
      });
    } catch (e) {
      console.warn("voice history upsert failed", e);
    }
  };

  // Redirect non-admins away with a toast. We do the check on mount so
  // the route itself renders quickly but never leaks the UI to non-admins.
  useEffect(() => {
    if (user === null) return; // still loading
    if (!isVoiceEnabledFor(user)) {
      toast.error("Voice Mode is currently in beta and restricted to designated admins.");
      navigate("/", { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    return () => {
      // Clean shutdown: cancel any recording + TTS in-flight.
      try { recRef.current?.cancel?.(); } catch { /* noop */ }
      try { speakRef.current?.abort?.(); } catch { /* noop */ }
      try { abortRef.current?.abort?.(); } catch { /* noop */ }
      try { directRef.current?.stop?.(); } catch { /* noop */ }
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const startElapsed = () => {
    const t0 = Date.now();
    setElapsed(0);
    timerRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - t0) / 1000));
    }, 250);
  };
  const stopElapsed = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  // Kick off the mic. Called by the big orb.
  const beginListening = async () => {
    if (!isVoiceSupported()) {
      toast.error("Voice recording isn't supported in this browser.");
      return;
    }
    // If a reply is currently being spoken, tapping the orb barges in:
    // cut TTS, cut any in-flight generation, then start listening.
    if (state === STATES.SPEAKING) {
      speakRef.current?.abort?.();
      speakRef.current = null;
    }
    if (state === STATES.THINKING) {
      abortRef.current?.abort?.();
      abortRef.current = null;
    }
    try {
      recRef.current = await startRecording();
      setState(STATES.LISTENING);
      startElapsed();
    } catch (e) {
      toast.error(e?.name === "NotAllowedError" ? "Microphone permission denied." : "Couldn't access microphone.");
    }
  };

  // --- Direct Voice (OpenAI Realtime) --------------------------------
  // Full-duplex WebRTC to gpt-4o-realtime. Once connected, mic + speaker
  // are wired straight to OpenAI; we only listen on the events channel
  // to keep an on-screen transcript. Barge-in works natively.
  const startDirectVoice = async () => {
    if (directRef.current) return; // already running
    setDirectConnecting(true);
    // Buffer the currently-forming user + assistant transcripts so we
    // append complete turns to `history` instead of one line per delta.
    let userBuffer = "";
    let assistantBuffer = "";
    // Per-turn audio blobs arrive from MediaRecorder BEFORE OpenAI's
    // transcript event that creates the history row. We queue them
    // here and drain into whichever row lands first. FIFO order —
    // blobs and rows are 1:1 per turn.
    const pendingUserBlobs = [];
    const pendingAssistantBlobs = [];
    const flushUser = () => {
      const text = userBuffer.trim();
      userBuffer = "";
      if (!text) return;
      // Pull the oldest queued blob for this role — matches the turn
      // whose transcript is arriving now. If the blob hasn't landed
      // yet (rare — MediaRecorder is nearly instant), the button
      // just won't appear on this row; that's still a graceful
      // degrade, no crash.
      const audio = pendingUserBlobs.shift() || null;
      setHistory((h) => {
        const next = [...h, { role: "user", content: text, audio }];
        persistTurn(next);
        return next;
      });
    };
    const flushAssistant = () => {
      const text = assistantBuffer.trim();
      assistantBuffer = "";
      if (!text) return;
      const audio = pendingAssistantBlobs.shift() || null;
      setHistory((h) => {
        const next = [...h, { role: "assistant", content: text, audio }];
        persistTurn(next);
        return next;
      });
      setAssistantText("");
    };

    const session = new RealtimeSession({
      onOpen: () => {
        // Fresh session — drop any stale session-blob from a
        // previous call so the download button always references
        // the current conversation.
        setDirectSessionBlob(null);
        setDirectConnected(true);
        setDirectConnecting(false);
        toast.success("Direct Voice connected — speak whenever.");
      },
      onClose: () => {
        setDirectConnected(false);
        setDirectConnecting(false);
        directRef.current = null;
      },
      onError: (e) => {
        toast.error(e?.message || "Direct Voice error.");
      },
      // Reliable session-wide audio capture. Fires with the mixed
      // Blob (mic + HBM) when the session ends. Held in state so
      // the "Save audio" button works post-hangup too.
      onSessionAudio: (blob) => {
        setDirectSessionBlob(blob);
      },
      // Attach per-turn audio Blobs to the newest matching history
      // row. Two paths, because MediaRecorder can emit its Blob EITHER
      // before or after the transcript event fires:
      //   1. Row already exists with no audio → attach immediately.
      //   2. No matching row yet → queue the blob; flushUser /
      //      flushAssistant will pick it up when the transcript lands.
      // Without the queue, blob-first turns silently dropped audio,
      // which is why the download button never appeared for many
      // users. FIFO order preserves turn alignment even if two turns
      // stack up in flight.
      onUserTurnAudio: (blob) => {
        setHistory((prev) => {
          const next = [...prev];
          for (let i = next.length - 1; i >= 0; i--) {
            if (next[i].role === "user" && !next[i].audio) {
              next[i] = { ...next[i], audio: blob };
              return next;
            }
          }
          pendingUserBlobs.push(blob);
          return prev;
        });
      },
      onAssistantTurnAudio: (blob) => {
        setHistory((prev) => {
          const next = [...prev];
          for (let i = next.length - 1; i >= 0; i--) {
            if (next[i].role === "assistant" && !next[i].audio) {
              next[i] = { ...next[i], audio: blob };
              return next;
            }
          }
          pendingAssistantBlobs.push(blob);
          return prev;
        });
      },
      onEvent: (msg) => {
        // OpenAI Realtime event shapes:
        //   response.audio_transcript.delta  — assistant speech transcript, streaming
        //   response.audio_transcript.done   — assistant turn complete
        //   conversation.item.input_audio_transcription.completed — user turn
        const t = msg?.type || "";
        if (t === "response.audio_transcript.delta") {
          assistantBuffer += (msg.delta || "");
          setAssistantText(assistantBuffer);
        } else if (t === "response.audio_transcript.done" || t === "response.done") {
          flushAssistant();
        } else if (t === "conversation.item.input_audio_transcription.completed") {
          userBuffer = msg.transcript || "";
          flushUser();
        }
      },
    });

    try {
      await session.start();
      directRef.current = session;
    } catch (e) {
      setDirectConnecting(false);
      toast.error(e?.message || "Couldn't start Direct Voice.");
    }
  };

  const stopDirectVoice = () => {
    try { directRef.current?.stop?.(); } catch { /* noop */ }
    directRef.current = null;
    setDirectConnected(false);
    setDirectConnecting(false);
    setAssistantText("");
  };

  const stopListeningAndSend = async () => {
    stopElapsed();
    if (!recRef.current) return;
    setState(STATES.TRANSCRIBING);
    let blob = null;
    try {
      blob = await recRef.current.stop();
      recRef.current = null;
    } catch (e) {
      toast.error("Recording ended abruptly.");
      setState(STATES.IDLE);
      return;
    }
    let text = "";
    try {
      const r = await transcribeAudio(blob);
      text = (r.text || "").trim();
    } catch (e) {
      if (e?.empty) {
        toast("Didn't catch that — try again.");
      } else {
        toast.error(e?.message || "Transcription failed.");
      }
      setState(STATES.IDLE);
      return;
    }
    if (!text) {
      toast.error("Didn't catch anything — try again.");
      setState(STATES.IDLE);
      return;
    }
    setTranscript(text);
    // Hand the user's own recording blob down to sendToModel so the
    // message row can offer a per-turn download button. Blob's kept as
    // a plain JS ref (React never diffs it, we only touch content).
    await sendToModel(text, blob);
  };

  // Cancel current recording without transcribing/sending.
  const cancelListening = () => {
    stopElapsed();
    try { recRef.current?.cancel?.(); } catch { /* noop */ }
    recRef.current = null;
    setState(STATES.IDLE);
  };

  const sendToModel = async (userText, userAudioBlob = null) => {
    setState(STATES.THINKING);
    setAssistantText("");

    // Attach the user's mic recording to their message so the row can
    // offer a "Download audio" button. `audio` is a plain Blob ref;
    // React never diffs it because we key rows by index + content.
    const userMsg = { role: "user", content: userText, audio: userAudioBlob || null };
    const nextHistory = [...history, userMsg];
    setHistory(nextHistory);

    const controller = new AbortController();
    abortRef.current = controller;

    let full = "";
    try {
      full = await streamChat({
        messages: nextHistory,
        sessionId: sessionIdRef.current,
        onChunk: (delta) => {
          full += delta;
          setAssistantText(stripMarkdown(full));
        },
        signal: controller.signal,
      });
    } catch (e) {
      if (e?.name === "AbortError") return;
      toast.error(e?.message || "Reply failed.");
      setState(STATES.IDLE);
      return;
    }

    const clean = stripMarkdown(full || "").trim();
    setAssistantText(clean);
    // Placeholder assistant row — audio blob gets attached below once
    // the TTS request finishes streaming its bytes.
    const assistantMsg = { role: "assistant", content: clean, audio: null };
    const finalHistory = [...nextHistory, assistantMsg];
    setHistory(finalHistory);

    // Fire-and-forget history persistence so a slow /api/history doesn't
    // delay the TTS playback.
    persistTurn(finalHistory);

    if (muted || !clean) {
      setState(STATES.IDLE);
      return;
    }

    // Speak the reply. Use Sarvam TTS default (Indian English). If the
    // reply is very long, the backend already trims to the last
    // sentence boundary under 2500 chars for the audio.
    setState(STATES.SPEAKING);
    const s = speakText(clean, {
      onBlob: (blob) => {
        // Attach the TTS audio to the exact assistant row we just
        // added so the row's download button lights up. We look up
        // the row by identity (React keeps object refs stable inside
        // the same setState cycle).
        setHistory((prev) => {
          const next = [...prev];
          const idx = next.lastIndexOf(assistantMsg);
          if (idx === -1) {
            // Fallback: last assistant row.
            for (let i = next.length - 1; i >= 0; i--) {
              if (next[i]?.role === "assistant") {
                next[i] = { ...next[i], audio: blob };
                break;
              }
            }
          } else {
            next[idx] = { ...next[idx], audio: blob };
          }
          return next;
        });
      },
    });
    speakRef.current = s;
    try {
      await s.promise;
    } catch (e) {
      // Non-fatal — user still has the text on screen.
      console.warn("TTS failed:", e?.message);
    } finally {
      speakRef.current = null;
      setState(STATES.IDLE);
    }
  };

  const stopSpeaking = () => {
    speakRef.current?.abort?.();
    speakRef.current = null;
    setState(STATES.IDLE);
  };

  const caption = voiceMode === "direct"
    ? (directConnecting ? "Connecting…" : directConnected ? "Live · speak freely" : "Tap to start Direct Voice")
    : {
        [STATES.IDLE]: history.length === 0 ? "Tap to speak" : "Tap to reply",
        [STATES.LISTENING]: "Listening…",
        [STATES.TRANSCRIBING]: "Transcribing…",
        [STATES.THINKING]: "Thinking…",
        [STATES.SPEAKING]: "Speaking…",
      }[state];

  const isBusy = voiceMode === "direct"
    ? directConnecting
    : (state === STATES.TRANSCRIBING || state === STATES.THINKING);

  const secondsLabel = `${Math.floor(elapsed / 60)}:${String(elapsed % 60).padStart(2, "0")}`;

  const orbClasses = voiceMode === "direct"
    ? (directConnected
        ? "bg-[var(--accent)] scale-[1.04] animate-pulse ring-8 ring-[var(--accent)]/20"
        : directConnecting
          ? "bg-[var(--accent)]/60"
          : "bg-[var(--accent)] hover:bg-[var(--accent-deep)]")
    : {
        [STATES.IDLE]:
          "bg-[var(--accent)] hover:bg-[var(--accent-deep)]",
        [STATES.LISTENING]:
          "bg-[var(--accent)] scale-[1.06] animate-pulse ring-8 ring-[var(--accent)]/20",
        [STATES.TRANSCRIBING]: "bg-[var(--accent)]/70",
        [STATES.THINKING]: "bg-[var(--accent)]/60",
        [STATES.SPEAKING]:
          "bg-[var(--accent)] scale-[1.02] ring-4 ring-[var(--accent)]/30 animate-pulse",
      }[state];

  const orbIcon = () => {
    if (voiceMode === "direct") {
      if (directConnecting) return <Loader2 size={44} className="animate-spin" />;
      if (directConnected) return <Square size={36} strokeWidth={2.5} fill="currentColor" />;
      return <Mic size={44} strokeWidth={2.2} />;
    }
    if (state === STATES.LISTENING) return <Square size={36} strokeWidth={2.5} fill="currentColor" />;
    if (state === STATES.TRANSCRIBING || state === STATES.THINKING)
      return <Loader2 size={44} className="animate-spin" />;
    if (state === STATES.SPEAKING) return <Volume2 size={44} strokeWidth={2.2} />;
    return <Mic size={44} strokeWidth={2.2} />;
  };

  const onOrbClick = () => {
    if (voiceMode === "direct") {
      if (directConnected) return stopDirectVoice();
      if (!directConnecting) return startDirectVoice();
      return;
    }
    if (state === STATES.IDLE) return beginListening();
    if (state === STATES.LISTENING) return stopListeningAndSend();
    if (state === STATES.SPEAKING) return stopSpeaking();
  };

  return (
    <div
      className="min-h-screen w-full bg-[var(--bg)] text-[var(--text)] flex flex-col"
      data-testid="voice-mode-page"
    >
      {/* Header */}
      <header className="w-full border-b border-[var(--border)] bg-[var(--bg-elevated)]">
        <div className="max-w-[840px] mx-auto flex items-center justify-between px-4 md:px-6 py-3">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-2 text-[var(--text-muted)] hover:text-[var(--text)] transition-colors"
            data-testid="voice-back"
            title="Back to chat"
          >
            <ArrowLeft size={16} />
            <span className="font-mono-ui text-[11px] uppercase tracking-[0.24em]">Home</span>
          </button>
          <div className="flex items-center gap-2 font-mono-ui text-[10px] uppercase tracking-[0.24em] text-[var(--text-muted)]">
            <span>Voice Mode · Beta</span>
            {directAllowed && (
              <div
                role="tablist"
                className="ml-2 inline-flex rounded-full border border-[var(--border)] overflow-hidden"
                data-testid="voice-mode-toggle"
              >
                <button
                  type="button"
                  onClick={() => {
                    if (voiceMode === "standard") return;
                    if (directConnected || directConnecting) stopDirectVoice();
                    setVoiceMode("standard");
                    if (typeof window !== "undefined") window.localStorage.setItem("hbm.voiceMode", "standard");
                  }}
                  data-testid="voice-mode-standard"
                  className={`px-3 py-1 transition-colors ${
                    voiceMode === "standard"
                      ? "bg-[var(--accent)] text-white"
                      : "text-[var(--text-muted)] hover:text-[var(--text)]"
                  }`}
                  title="Whisper STT + Sarvam TTS. Uses your voice_beta access."
                >
                  Standard
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (voiceMode === "direct") return;
                    setVoiceMode("direct");
                    if (typeof window !== "undefined") window.localStorage.setItem("hbm.voiceMode", "direct");
                  }}
                  data-testid="voice-mode-direct"
                  className={`px-3 py-1 transition-colors ${
                    voiceMode === "direct"
                      ? "bg-[var(--accent)] text-white"
                      : "text-[var(--text-muted)] hover:text-[var(--text)]"
                  }`}
                  title="OpenAI Realtime — full-duplex voice-to-voice. Requires direct_voice_beta."
                >
                  Direct
                </button>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Session-audio download. Uses the reliable Web Audio
                mix recorder (mic + HBM into one stream) — bypasses
                the MediaRecorder-on-WebRTC-track flakiness that
                caused earlier per-turn downloads to be silent. Works
                mid-session (grabs whatever's been captured so far)
                AND after the session ends. */}
            <button
              type="button"
              onClick={async () => {
                // Prefer the live session blob when Direct Voice is
                // running or was just running. Falls back to per-turn
                // blobs (Standard Voice path) if the session mix
                // recorder never engaged.
                let blob = null;
                try {
                  const p = directRef.current?.getSessionBlob?.();
                  // getSessionBlob is async — awaits internal
                  // requestData() so the tail chunk lands. Support
                  // both sync (null) and async return.
                  blob = p && typeof p.then === "function" ? await p : p;
                } catch (err) {
                  console.error("[HBM voice] getSessionBlob threw:", err);
                }
                if (!blob && directSessionBlob) blob = directSessionBlob;
                if (blob) {
                  if (blob.size < 1000) {
                    // Empty-ish blob — MediaRecorder didn't capture
                    // anything usable. Tell the user WHY instead of
                    // handing them a broken file.
                    toast.error(
                      "Recording was too short to save — speak for at least 2 seconds, then try again.",
                    );
                    return;
                  }
                  downloadAudio(blob, {
                    role: "session",
                    label: titleRef.current || "hbm-voice",
                  });
                  toast.success(`Downloaded ${Math.round(blob.size / 1024)} KB session audio.`);
                  return;
                }
                // No mix recording available — try per-turn blobs
                // (Standard Voice mode uses these; Direct Voice adds
                // them as a bonus alongside the mix).
                const withAudio = history.filter((m) => m.audio);
                if (!withAudio.length) {
                  toast.error(
                    directConnected
                      ? "No audio captured yet — speak for a few seconds, then try again."
                      : "No audio to download. Start a Direct Voice call and speak first.",
                  );
                  return;
                }
                toast.success(`Saving ${withAudio.length} audio clip${withAudio.length > 1 ? "s" : ""}…`);
                for (let i = 0; i < withAudio.length; i++) {
                  const m = withAudio[i];
                  try {
                    downloadAudio(m.audio, {
                      role: m.role === "user" ? "you" : "hbm",
                      label: (m.content || "").slice(0, 40),
                    });
                  } catch { /* skip a broken blob; keep going */ }
                  await new Promise((r) => setTimeout(r, 250));
                }
              }}
              disabled={
                !directConnected &&
                !directSessionBlob &&
                !history.some((m) => m.audio)
              }
              data-testid="voice-download-all-audio"
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[var(--accent)]/40 text-[var(--accent)] hover:bg-[var(--accent)]/10 hover:border-[var(--accent)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              title={
                directConnected
                  ? "Download the mixed session recording so far (mic + HBM)"
                  : directSessionBlob
                    ? "Download the last session recording"
                    : "Download every turn's audio"
              }
            >
              <Download size={13} />
              <span className="font-mono-ui text-[10px] uppercase tracking-[0.2em]">
                {directConnected
                  ? (captureStatus === "recording"
                      ? `Save audio · ${(captureBytes / 1024).toFixed(0)} KB`
                      : "Save audio · recorder failed")
                  : directSessionBlob
                    ? `Save audio · ${(directSessionBlob.size / 1024).toFixed(0)} KB`
                    : "Save audio"}
              </span>
            </button>
            <button
              type="button"
              onClick={() => {
                if (history.length === 0) {
                  toast("Nothing to save yet.");
                  return;
                }
                downloadTranscript(history, { title: titleRef.current || "voice-session" });
              }}
              disabled={history.length === 0}
              data-testid="voice-download-transcript"
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[var(--border)] text-[var(--text-muted)] hover:text-[var(--text)] hover:border-[var(--text)]/40 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              title="Download full transcript as .txt"
            >
              <FileDown size={13} />
              <span className="font-mono-ui text-[10px] uppercase tracking-[0.2em]">
                Transcript
              </span>
            </button>
            {!directConnected && !directSessionBlob && !history.some((m) => m.audio) && (
              <span className="ml-1 text-[11px] italic whitespace-nowrap text-[var(--text-muted)]/70">
                ▶ Start a Direct Voice call &amp; speak to enable download
              </span>
            )}
            <button
              type="button"
              onClick={() => {
                if (!muted) speakRef.current?.abort?.();
                setMuted((m) => !m);
              }}
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border transition-colors ${
                muted
                  ? "border-[var(--border)] text-[var(--text-muted)] hover:text-[var(--text)]"
                  : "border-[var(--accent)]/30 text-[var(--accent)]"
              }`}
              data-testid="voice-mute"
              title={muted ? "Unmute replies" : "Mute replies"}
            >
              {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
              <span className="font-mono-ui text-[10px] uppercase tracking-[0.2em]">
                {muted ? "Muted" : "Voice"}
              </span>
            </button>
          </div>
        </div>
      </header>

      {/* Transcript stack */}
      <main className="flex-1 w-full">
        <div className="max-w-[720px] mx-auto px-4 md:px-6 py-8 space-y-6">
          {history.length === 0 && state === STATES.IDLE && (
            <div className="text-center pt-16">
              <h1 className="font-prose text-3xl md:text-4xl leading-tight text-[var(--text)] mb-3">
                Say what&apos;s on your mind.
              </h1>
              <p className="font-ui text-sm text-[var(--text-muted)] max-w-md mx-auto">
                Tap the orb, speak in any language — HBM will answer aloud. Tap again to interrupt.
              </p>
            </div>
          )}

          {history.length > 0 && (
            <div className="space-y-5" data-testid="voice-transcript">
              {history.map((m, i) => (
                <div key={i} className="space-y-2" data-testid={`voice-msg-${i}`}>
                  <div className="font-mono-ui text-[10px] uppercase tracking-[0.24em] text-[var(--text-muted)]">
                    {m.role === "user" ? "You" : "HBM"}
                  </div>
                  <div className="font-prose text-[15px] md:text-base leading-relaxed whitespace-pre-wrap text-[var(--text)]">
                    {m.content}
                  </div>
                  {/* Per-turn audio download. Always rendered so the
                      affordance is visible even before MediaRecorder
                      finishes flushing the last chunk. Once the Blob
                      lands, the button becomes active. */}
                  <div className="flex items-center gap-2 pt-1">
                    <button
                      type="button"
                      onClick={() =>
                        m.audio &&
                        downloadAudio(m.audio, {
                          role: m.role === "user" ? "you" : "hbm",
                          label: (m.content || "").slice(0, 40),
                        })
                      }
                      disabled={!m.audio}
                      data-testid={`voice-msg-download-${i}`}
                      title={
                        m.audio
                          ? `Download ${m.role === "user" ? "your" : "HBM's"} audio`
                          : "Audio still processing…"
                      }
                      className={
                        "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border transition-colors " +
                        (m.audio
                          ? "border-[var(--accent)]/40 text-[var(--accent)] hover:bg-[var(--accent)]/10 hover:border-[var(--accent)]"
                          : "border-[var(--border)] text-[var(--text-dim)] opacity-60 cursor-not-allowed")
                      }
                    >
                      <Download size={12} />
                      <span className="font-mono-ui text-[10px] tracking-[0.2em] uppercase">
                        {m.audio ? "Download audio" : "Audio saving…"}
                      </span>
                    </button>
                  </div>
                </div>
              ))}

              {/* In-flight assistant text while THINKING/SPEAKING */}
              {(state === STATES.THINKING || state === STATES.SPEAKING) && assistantText && (
                <div className="space-y-1" data-testid="voice-live-reply">
                  <div className="font-mono-ui text-[10px] uppercase tracking-[0.24em] text-[var(--text-muted)]">
                    HBM
                  </div>
                  <div className="font-prose text-[15px] md:text-base leading-relaxed whitespace-pre-wrap text-[var(--text)]">
                    {assistantText}
                  </div>
                </div>
              )}
            </div>
          )}

          {state === STATES.LISTENING && transcript && (
            <div className="opacity-60">
              <div className="font-mono-ui text-[10px] uppercase tracking-[0.24em] text-[var(--text-muted)] mb-1">
                Previous
              </div>
              <div className="font-prose text-[14px] leading-relaxed text-[var(--text-muted)]">
                {transcript}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Orb + status */}
      <footer className="w-full border-t border-[var(--border)] bg-[var(--bg-elevated)] py-8">
        <div className="max-w-[720px] mx-auto px-4 md:px-6 flex flex-col items-center gap-4">
          <button
            type="button"
            onClick={onOrbClick}
            disabled={isBusy}
            aria-label={caption}
            data-testid="voice-orb"
            className={`w-28 h-28 md:w-32 md:h-32 rounded-full flex items-center justify-center text-white shadow-2xl transition-all duration-300 ${orbClasses} disabled:cursor-not-allowed`}
          >
            {orbIcon()}
          </button>

          <div className="flex items-center gap-3">
            <div
              className="font-mono-ui text-[11px] uppercase tracking-[0.28em] text-[var(--text)]"
              data-testid="voice-caption"
            >
              {caption}
            </div>
            {state === STATES.LISTENING && (
              <span
                className="font-mono-ui text-[11px] tabular-nums text-[var(--accent)]"
                data-testid="voice-elapsed"
              >
                {secondsLabel}
              </span>
            )}
          </div>

          {state === STATES.LISTENING && (
            <button
              type="button"
              onClick={cancelListening}
              className="font-mono-ui text-[10px] uppercase tracking-[0.24em] text-[var(--text-muted)] hover:text-[var(--accent)] transition-colors"
              data-testid="voice-cancel"
            >
              Cancel
            </button>
          )}
        </div>
      </footer>
    </div>
  );
}
